<template>
    <transition name="fade">
        <aside
            v-if="isVisible"
            ref="container"
            class="col-controls"
            :class="[stateClass, positionClasses]"
        >
            <button
                ref="handle"
                class="col-controls__button col-controls__button--toggle"
                type="button"
                :title="handleTitle"
                @click.stop="toggleCollapsed"
            >
                <font-awesome-icon :icon="['fas', handleIcon]" />
            </button>

            <button
                v-if="$listeners.select"
                class="col-controls__button"
                type="button"
                title="Select Component"
                @click.stop="$emit('select')"
            >
                <font-awesome-icon :icon="['fal', 'bullseye']" />
            </button>

            <span v-if="numElements >= 0" class="col-controls__count">
                <strong>{{ visibleIndex + 1 }}</strong> of <strong>{{ numElements }}</strong>
            </span>

            <button
                v-if="$listeners.add && !dynamicMapPreviewMode"
                class="col-controls__button"
                type="button"
                title="Add Item"
                @click.stop="$emit('add')"
            >
                <font-awesome-icon :icon="['fal', 'plus']" />
            </button>

            <button
                v-if="$listeners.delete && !dynamicMapPreviewMode"
                class="col-controls__button"
                type="button"
                title="Delete Item"
                @click.stop="$emit('delete', visibleIndex)"
            >
                <font-awesome-icon :icon="['fal', 'minus']" />
            </button>

            <button
                v-if="$listeners.star && !dynamicMapPreviewMode"
                class="col-controls__button"
                type="button"
                title="Set as Default"
                @click.stop="$emit('star', visibleIndex)"
            >
                <font-awesome-icon :icon="starIcon" />
            </button>

            <button
                v-if="$listeners.previous"
                class="col-controls__button"
                type="button"
                title="Previous"
                @click.stop="$emit('previous')"
            >
                <font-awesome-icon :icon="['fal', 'chevron-left']" />
            </button>

            <button
                v-if="$listeners.next"
                class="col-controls__button"
                type="button"
                title="Next"
                @click.stop="$emit('next')"
            >
                <font-awesome-icon aria-hidden="true" :icon="['fal', 'chevron-right']" />
            </button>

            <button
                v-if="$listeners.decrement"
                class="col-controls__button"
                type="button"
                title="Move Down"
                @click.stop="$emit('decrement')"
            >
                <font-awesome-icon :icon="['fal', 'chevron-down']" />
            </button>

            <button
                v-if="$listeners.increment"
                class="col-controls__button"
                type="button"
                title="Move Up"
                @click.stop="$emit('increment')"
            >
                <font-awesome-icon aria-hidden="true" :icon="['fal', 'chevron-up']" />
            </button>
        </aside>
    </transition>
</template>

<script>
import { mapState } from 'vuex';
import { STATE_CLASSES } from '@/data/constants/classes';

const publicMode = process.env.VUE_APP_ENV === 'public';

export default {
    name: 'collection-controls',

    props: {
        visibleIndex: {
            type: Number,
            default: 0,
        },

        defaultIndex: {
            type: Number,
            default: 0,
        },

        numElements: {
            type: Number,
            default: -1,
        },

        location: {
            type: String,
            default: 'inside',
        },

        position: {
            type: String,
            default: 'top-right',
        },
    },

    data() {
        return {
            isCollapsed: false,
        };
    },

    computed: {
        ...mapState({
            guidedMode: ({ page }) => page && page.guidedMode,
            previewMode: state => state.previewMode,
            selectedRendering: ({ page }) => page && page.selectedRendering,
            dynamicMapPreviewMode: state => state.dynamicMapPreviewMode,
        }),

        isVisible() {
            if (publicMode || this.previewMode || this.guidedMode) return false;

            const isSelected = this.$parent.$parent === this.selectedRendering;
            const isGlobalContent = this.$parent.$parent.globalContent;

            return this.$route.name === 'components'
                    || (this.$route.name === 'editor' && isSelected && !isGlobalContent);
        },

        handleIcon() {
            return this.isCollapsed ? 'caret-left' : 'caret-right';
        },

        handleTitle() {
            return this.isCollapsed ? 'Collapse' : 'Expand';
        },

        stateClass() {
            return this.isCollapsed ? STATE_CLASSES.COLLAPSED : STATE_CLASSES.EXPANDED;
        },

        positionClasses() {
            const classes = [];

            if (this.location) classes.push(`is-${ this.location }`);
            if (this.position) classes.push(`is-${ this.position }`);

            return classes;
        },

        starIcon() {
            return this.defaultIndex === this.visibleIndex
                ? ['fas', 'star']
                : ['fal', 'star'];
        },
    },

    mounted() {
        if (this.isVisible) this.storeElementWidths();
    },

    methods: {
        // store the handle and container widths in a CSS variable so the collapse/expand animation will always work properly
        // ... even if any styles that effect the width are inadvertently changed in the future
        async storeElementWidths() {
            await this.$nextTick();

            const containerWidth = this.$refs.container.offsetWidth;
            const handleWidth = this.$refs.handle.offsetWidth;

            document.documentElement.style.setProperty('--collectionContainer-width', `${ containerWidth }px`);
            document.documentElement.style.setProperty('--collectionContainer--handleWidth', `${ handleWidth }px`);
        },

        toggleCollapsed() {
            this.isCollapsed = !this.isCollapsed;
        },
    },
};
</script>


<style lang="scss" scoped>
@import "@/styles/_includes.scss";

.col-controls {
    $this: &;
    color: var(--elementToolbar-color);
    background: var(--elementToolbar-backgroundColor);
    position: absolute;
    z-index: 10;
    transition: opacity 0.45s, max-width 0.25s;
    font-size: 1rem;
    font-family: $default-font-family;
    max-width: var(--collectionContainer--width);
    white-space: nowrap;

    &.fade-enter,
    &.fade-leave-to {
        opacity: 0;
    }

    &.is-outside.is-top-left { top: -100%; left: 0; }
    &.is-outside.is-top-right { top: -100%; right: 0; }
    &.is-outside.is-bottom-left { bottom: -100%; left: 0; }
    &.is-outside.is-bottom-right { bottom: -100%; right: 0; }
    &.is-inside.is-top-left { top: 0; left: 0; }
    &.is-inside.is-top-right { top: 0; right: 0; }
    &.is-inside.is-bottom-left { bottom: 0; left: 0; }
    &.is-inside.is-bottom-right { bottom: 0; right: 0; }

    &.is-location-hero-tabs.is-position-hero-tabs { bottom: 100%; right: 0;}

    &.#{ $is-collapsed } {
        max-width: var(--collectionContainer--handleWidth);
    }

    &__count {
        padding: 0.5em;
        font-size: 1em;

        strong {
            margin: 0 0.125em;
        }
    }

    &__button {
        border: none;
        padding: 0.5em 0.875em;
        font-size: 1em;

        &:hover {
            background: var(--elementToolbar-hoverBackgroundColor);
            color: var(--elementToolbar-hoverColor);
        }

        &--toggle {
            padding: 0.5em;
            background: var(--elementToolbar-handleBackgroundColor);
            color: var(--elementToolbar-handleColor);

            &:hover {
                background: var(--elementToolbar-handleBackgroundColor);
                color: var(--elementToolbar-handleColor);
            }

            #{ $this }.#{ $is-expanded } & {
                svg {
                    position: relative;
                    right: -1px;
                }
            }
        }
    }
}
</style>
